import api from './api';
import type { SiteIdentity } from '@foundlio/pack';

/**
 * The dashboard's read/write side of the print-ready welcome-pack kit —
 * mirrors `websiteService.ts`'s idiom (shared `api` instance, unwrap
 * `response.data.data`). See `apps/client-portal/backend/src/routes/printKit.ts`
 * for the endpoints; the slug is resolved server-side from the caller's own
 * account, never sent from here.
 */

export type PieceId = 'card' | 'hanger' | 'magnet' | 'sign';

export interface KitPiece {
  id: PieceId;
  file: string;
  label: string;
  trimIn: [number, number];
  bleedIn: number;
  pageIn: [number, number];
  pages: number;
  bytes: number;
  sha256: string;
  logoOk: boolean | null;
  warnings: string[];
}

export interface KitManifest {
  slug: string;
  scene: string;
  trade: string;
  generatedAt: string;
  generatorVersion: string;
  baseUrl: string;
  rgbNote: string;
  pieces: KitPiece[];
}

export type PrintKitStatus = 'ready' | 'pending' | 'none';

export interface PrintKitState {
  status: PrintKitStatus;
  manifest: KitManifest | null;
  identity: SiteIdentity | null;
}

export const printKitService = {
  getStatus: async (): Promise<PrintKitState> => {
    const response = await api.get('/api/print-kit');
    return response.data.data as PrintKitState;
  },

  regenerate: async (): Promise<{ status: PrintKitStatus }> => {
    const response = await api.post('/api/print-kit/regenerate');
    return response.data.data as { status: PrintKitStatus };
  },

  /** The URL a plain `<a download>` (or window.open) fetches the piece from. */
  pieceUrl: (pieceId: PieceId): string => `/api/print-kit/piece/${pieceId}`,

  /**
   * Downloads one piece through the authenticated `api` instance (a bare
   * `<a href>` would hit the API host with no auth header) and saves it via a
   * throwaway object URL.
   */
  downloadPiece: async (pieceId: PieceId, filename: string): Promise<void> => {
    const response = await api.get(`/api/print-kit/piece/${pieceId}`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
